import { motion } from 'framer-motion';
import { ArrowRight, ArrowDown } from 'lucide-react';
import { Container } from '../ui/Container';
import { EyebrowPill } from '../ui/EyebrowPill';
import { Button } from '../ui/Button';
import { GlowOrb } from '../ui/GlowOrb';
import { HeroTerminal } from './HeroTerminal';
import { HeroMarquee } from './HeroMarquee';
import { track } from '../../lib/tracking';
import { useT } from '../../i18n/LanguageContext';

export function Hero() {
  const t = useT();
  const headlineLines: { text: string; emphasis?: boolean; muted?: boolean }[] = [
    { text: t.hero.headline1 },
    { text: t.hero.headline2, muted: true },
    { text: t.hero.headline3 },
    { text: t.hero.headline4, emphasis: true },
  ];
  return (
    <section
      id="top"
      className="relative pt-28 md:pt-32 pb-8 overflow-hidden bg-hero-bg"
    >
      {/* Glow background */}
      <GlowOrb
        size="xl"
        color="blue"
        className="left-1/2 top-1/3 -translate-x-1/2 opacity-40"
      />
      <GlowOrb
        size="lg"
        color="cyan"
        className="-right-40 top-20 opacity-20"
      />
      <GlowOrb
        size="md"
        color="lavender"
        className="-left-20 bottom-0 opacity-15"
      />

      {/* Grid background */}
      <div
        aria-hidden
        className="absolute inset-0 opacity-[0.04] pointer-events-none"
        style={{
          backgroundImage:
            'linear-gradient(rgba(255,255,255,0.5) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.5) 1px, transparent 1px)',
          backgroundSize: '64px 64px',
          maskImage:
            'radial-gradient(ellipse at 50% 30%, black 30%, transparent 70%)',
        }}
      />

      <Container className="relative">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">
          {/* Left: copy */}
          <div className="lg:col-span-7 flex flex-col gap-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <EyebrowPill>{t.hero.eyebrow}</EyebrowPill>
            </motion.div>

            <h1 className="heading-h1 text-white text-4xl md:text-5xl lg:text-6xl xl:text-[68px]">
              {headlineLines.map((line, i) => (
                <motion.span
                  key={i}
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{
                    duration: 0.7,
                    delay: 0.2 + i * 0.12,
                    ease: [0.4, 0, 0, 1],
                  }}
                  className="block"
                >
                  {line.muted ? (
                    <span className="text-ink-600">{line.text}</span>
                  ) : line.emphasis ? (
                    <span className="gradient-text">{line.text}</span>
                  ) : (
                    line.text
                  )}
                </motion.span>
              ))}
            </h1>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.7 }}
              className="text-base md:text-lg text-ink-400 leading-relaxed max-w-2xl"
            >
              {t.hero.subheadPart1}{' '}
              <strong className="text-white font-semibold">
                {t.hero.subheadAmount}
              </strong>
              {t.hero.subheadPart2}
              <strong className="text-white font-semibold">
                {t.hero.subheadBrand1}
              </strong>
              {t.hero.subheadPart3}
              <strong className="text-white font-semibold">
                {t.hero.subheadBrand2}
              </strong>
              {t.hero.subheadPart4}
              <strong className="text-white font-semibold">
                {t.hero.subheadBrand3}
              </strong>
              {t.hero.subheadPart5}
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.85 }}
              className="flex flex-col sm:flex-row items-start sm:items-center gap-4"
            >
              <Button
                variant="primary"
                size="lg"
                icon={<ArrowRight className="h-4 w-4" />}
                iconPosition="right"
                className="group"
                onClick={() => {
                  track('cta_calcular_click', { source_section: 'hero' });
                  document
                    .getElementById('calculadora')
                    ?.scrollIntoView({ behavior: 'smooth' });
                }}
              >
                {t.hero.ctaPrimary}
              </Button>

              <a
                href="#arquitetura"
                className="inline-flex items-center gap-2 text-sm text-ink-400 hover:text-white transition-colors"
              >
                <span>{t.hero.ctaSecondary}</span>
                <ArrowDown className="h-3.5 w-3.5" />
              </a>
            </motion.div>

            {/* Mini stats */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.7, delay: 1.0 }}
              className="flex items-center gap-6 pt-4 border-t border-white/5"
            >
              <Stat label={t.hero.statBlocks} value={t.hero.statBlocksValue} />
              <span className="h-8 w-px bg-white/10" aria-hidden />
              <Stat label={t.hero.statSteps} value={t.hero.statStepsValue} />
              <span className="h-8 w-px bg-white/10" aria-hidden />
              <Stat
                label={t.hero.statStatus}
                value={t.hero.statStatusValue}
                tone="cyan"
              />
            </motion.div>
          </div>

          {/* Right: terminal */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 1.0, delay: 0.6, ease: [0.44, 0, 0.56, 1] }}
            className="lg:col-span-5"
          >
            <HeroTerminal />
          </motion.div>
        </div>
      </Container>

      <div className="mt-12 md:mt-16">
        <HeroMarquee />
      </div>
    </section>
  );
}

function Stat({
  label,
  value,
  tone = 'default',
}: {
  label: string;
  value: string;
  tone?: 'default' | 'cyan';
}) {
  return (
    <div className="flex flex-col">
      <span className="text-[10px] uppercase tracking-widest text-ink-600">
        {label}
      </span>
      <span
        className={
          tone === 'cyan'
            ? 'text-sm font-mono text-brand-300'
            : 'text-sm font-mono text-white'
        }
      >
        {value}
      </span>
    </div>
  );
}
